import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { ButtonsOptions, ColorOptions, HorizontalAlignmentOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { GSAPModule } from '@df/ui/display/gsap/gsap.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';
import { BASIC_COMPONENT_FIELDS, CMS_COMPONENT_CWV_CONFIG, ICmsComponentCwvConfig } from '../../../../config/component.config';
import { MultiColumnBannerAbstract } from './multi-column-banner.abstract';

export interface PageComponentMultiColumnBannerData extends NICmsContentComponent.IData, ICmsComponentCwvConfig {
    items: PageComponentMultiColumnBannerItemData[];
    grid_gap: number;
    wrap_padding: number;
}

export interface PageComponentMultiColumnBannerItemData {
    image: string;
    video_id: string;
    cta_title?: string;
    cta_title_text_color?: string;
    cta_text: string;
    cta_link: string;
    cta_text_color?: string;
    cta_style: string;
    display_arrow?: boolean;
    title?: string;
    title_text_color?: string;
    cta_text_1?: string;
    cta_link_1?: string;
    cta_style_1: string;
    cta_text_1_color?: string;
    display_arrow_1?: string;
    text_align?: string;
}

export const additionalFields = [
    // additional
    <NICmsContentField.IField>{
        type: NICmsContentField.EFieldType.label,
        key: 'section-additional',
        title: 'Additional'
    },
    <NICmsContentField.INumberField>{
        key: 'grid_gap',
        type: NICmsContentField.EFieldType.number,
        title: 'Gap',
        description: 'Gap between items',
        helper: 'Max gap is 50px',
        min: 0,
        max: 50,
        step: 1,
        default: 3,
        sanitize: (input: number) => {
            const value = Math.max(0, Math.min(input, 50));

            if (input < 0) {
                return {
                    error: 'Gap cannot be less than 0',
                    value
                };
            } else if (input > 50) {
                return {
                    error: 'Gap cannot be more than 50',
                    value
                };
            }

            return value;
        },
        required: true
    },
    <NICmsContentField.INumberField>{
        key: 'wrap_padding',
        type: NICmsContentField.EFieldType.number,
        title: 'Wrap Padding',
        description: 'Padding on the left and right side of the component wrapper',
        helper: 'Max wrap padding is 10px',
        min: 0,
        max: 10,
        step: 1,
        default: 0,
        sanitize: (input: number) => {
            const value = Math.max(0, Math.min(input, 10));

            if (input < 0) {
                return {
                    error: 'Wrap padding cannot be less than 0',
                    value
                };
            } else if (input > 10) {
                return {
                    error: 'Wrap padding cannot be more than 10',
                    value
                };
            }

            return value;
        },
        required: true
    },
    ...BASIC_COMPONENT_FIELDS,
    ...CMS_COMPONENT_CWV_CONFIG
];

export const itemsFields = [
    // media
    <NICmsContentField.IField>{
        type: NICmsContentField.EFieldType.label,
        key: 'section-media',
        title: 'Media'
    },
    <NICmsContentField.IField>{
        key: 'image',
        type: NICmsContentField.EFieldType.image,
        title: 'Image',
        description: 'Can be used also as a background for the video'
    },
    <NICmsContentField.IField>{
        key: 'video_id',
        type: NICmsContentField.EFieldType.text,
        title: 'Video ID or URL',
        description: 'Vimeo video ID or URL',
        default: ''
    },
    // CTA button
    <NICmsContentField.IField>{
        key: 'cta_title',
        type: NICmsContentField.EFieldType.text,
        title: 'Title',
        default: 'Lorem ipsum'
    },
    <NICmsContentField.IChoiceField>{
        key: 'cta_title_text_color',
        type: NICmsContentField.EFieldType.choice,
        title: 'Title text color',
        options: ColorOptions
    },
    <NICmsContentField.IField>{
        type: NICmsContentField.EFieldType.label,
        key: 'section-cta-button',
        title: 'CTA'
    },
    <NICmsContentField.IField>{
        key: 'cta_text',
        type: NICmsContentField.EFieldType.text,
        title: 'CTA Text',
        default: 'Button label'
    },
    <NICmsContentField.IField>{
        key: 'cta_link',
        type: NICmsContentField.EFieldType.text,
        title: 'CTA Link',
        default: '/'
    },
    <NICmsContentField.IChoiceField>{
        key: 'cta_text_color',
        type: NICmsContentField.EFieldType.choice,
        title: 'CTA text color',
        options: ColorOptions
    },
    <NICmsContentField.IChoiceField>{
        key: 'cta_style',
        type: NICmsContentField.EFieldType.choice,
        title: 'CTA Style',
        options: ButtonsOptions,
        default: 'button-inline has-icon'
    },
    <NICmsContentField.IField>{
        key: 'display_arrow',
        type: NICmsContentField.EFieldType.toggle,
        title: 'Display arrow',
        default: false
    },
    // CTA text
    <NICmsContentField.IField>{
        type: NICmsContentField.EFieldType.label,
        key: 'section-cta',
        title: 'CTA below image'
    },
    <NICmsContentField.IField>{
        key: 'title',
        type: NICmsContentField.EFieldType.text,
        title: 'Title'
    },
    <NICmsContentField.IChoiceField>{
        key: 'title_text_color',
        type: NICmsContentField.EFieldType.choice,
        title: 'Title text color',
        options: ColorOptions
    },
    <NICmsContentField.IField>{
        key: 'cta_text_1',
        type: NICmsContentField.EFieldType.text,
        title: 'CTA Text'
    },
    <NICmsContentField.IField>{
        key: 'cta_link_1',
        type: NICmsContentField.EFieldType.text,
        title: 'CTA Link',
        default: '/'
    },
    <NICmsContentField.IChoiceField>{
        key: 'cta_text_1_color',
        type: NICmsContentField.EFieldType.choice,
        title: 'CTA text color',
        options: ColorOptions
    },
    <NICmsContentField.IChoiceField>{
        key: 'cta_style_1',
        type: NICmsContentField.EFieldType.choice,
        title: 'CTA Style',
        options: ButtonsOptions,
        default: 'button-inline has-icon'
    },
    <NICmsContentField.IField>{
        key: 'display_arrow_1',
        type: NICmsContentField.EFieldType.toggle,
        title: 'Display arrow',
        default: false
    },
    <NICmsContentField.IChoiceField>{
        key: 'text_align',
        type: NICmsContentField.EFieldType.choice,
        title: 'Text Alignment',
        options: HorizontalAlignmentOptions,
        default: 'left'
    },
    ...CMS_COMPONENT_CWV_CONFIG
];

export const fields = [
    <NICmsContentField.IRepeaterField>{
        type: NICmsContentField.EFieldType.repeater,
        key: 'items',
        title: 'Items',
        itemName: 'Item',
        itemNameField: 'cta_text',
        defaultSize: 2,
        maxSize: 4,
        fields: itemsFields
    },
    ...additionalFields
];

@ICmsComponent<PageComponentMultiColumnBannerComponent>({
    id: 'multi-column-banner-v1',
    name: 'Multi Column Banner (Variant 1)',
    image: 'assets/images/cms/multi-column-banner.png',
    description: 'Two-column banner',
    tags: ['Images', 'Banner', 'Video'],
    fields
})
@Component({
    selector: 'page-component-multi-column-banner',
    templateUrl: './multi-column-banner.html',
    styleUrl: './multi-column-banner.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        GridModule,
        GSAPModule,
        HrefComponent,
        ICmsClientModule,
        LocalVimeoPlayerComponent,
        ShellComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentMultiColumnBannerComponent extends MultiColumnBannerAbstract {}
